import { z } from 'zod';

export const Character = z.object({
  name: z.string(),
  color: z.string().regex(/^#[0-9a-f]{6}$/i),
  name_zh: z.string().optional(),
});

export const Meta = z.object({
  id: z.string().min(1),
  title: z.string().min(1),
  version: z.number().int().positive(),
});

const beatBase = { id: z.string().min(1) };

export const VfxKind = z.enum([
  'shake',          // Brief camera shake — anger, shock
  'glitch',         // Memory corruption flicker — fractured recollection
  'glitch-heavy',   // Intense glitch burst — reality-shattering reveal
  'flash-white',    // White flash — intrusive memory surfacing
  'flash-red',      // Red flash — danger, violence
  'vignette',       // Slow vignette pulse — dread, claustrophobia
  'desaturate',     // Colour drains away — hopelessness, dissociation
  'bloom-warm',     // Warm light bloom — truth returning, restoration
  'data-cascade',   // Particle data rain — files flooding out
  'darkness-creep', // Darkness encroaches from edges — erasure, loss
]);
export type VfxKind = z.infer<typeof VfxKind>;

export const SayBeat = z.object({
  ...beatBase,
  kind: z.literal('say'),
  speaker: z.string(),
  mood: z.string().optional(),
  text: z.string().min(1),
  bg: z.string().optional(),
  portrait: z.string().optional(),
  fx: VfxKind.optional(),
});

export const SceneBeat = z.object({
  ...beatBase,
  kind: z.literal('scene'),
  bg: z.string(),
  transition: z.enum(['fade', 'cut', 'dip-to-black', 'thread-pull']).optional(),
  /** Optional ambient music mood id, e.g. 'domestic-warm', 'archive-tension'. */
  bgm: z.string().optional(),
  fx: VfxKind.optional(),
});

export const SfxBeat = z.object({
  ...beatBase,
  kind: z.literal('sfx'),
  sfx: z.string(),
});

export const SetFlagBeat = z.object({
  ...beatBase,
  kind: z.literal('setFlag'),
  flag: z.string(),
  value: z.union([z.boolean(), z.string(), z.number()]),
});

export const FlagCondition = z.object({
  flag: z.string().min(1),
  value: z.union([z.boolean(), z.string(), z.number()]),
  op: z.enum(['eq', 'neq']).optional(), // defaults to 'eq'
});

export const ChoiceOption = z.object({
  label: z.string().min(1),
  goto: z.string().min(1),
  setFlag: z
    .record(z.string(), z.union([z.boolean(), z.string(), z.number()]))
    .optional(),
  condition: FlagCondition.optional(),
});

export const ChoiceBeat = z.object({
  ...beatBase,
  kind: z.literal('choice'),
  prompt: z.string(),
  options: z.array(ChoiceOption).min(2).max(4),
});

export const GotoBeat = z.object({
  ...beatBase,
  kind: z.literal('goto'),
  goto: z.string(),
});

export const IfFlagBeat = z.object({
  ...beatBase,
  kind: z.literal('ifFlag'),
  flag: z.string(),
  value: z.union([z.boolean(), z.string(), z.number()]),
  goto: z.string(),
  else: z.string(),
});

export const EndBeat = z.object({
  ...beatBase,
  kind: z.literal('end'),
  epilogue: z.string(),
});

export const WaitBeat = z.object({
  ...beatBase,
  kind: z.literal('wait'),
  duration_ms: z.number().int().min(500),
});

export const BondRingUpdateBeat = z.object({
  ...beatBase,
  kind: z.literal('bond_ring_update'),
  visible_threads: z.array(z.string()).min(1),
  mode: z.enum(['constellation', 'highlight', 'fade']).default('constellation'),
});

export const CrestHudSetBeat = z.object({
  ...beatBase,
  kind: z.literal('crest_hud_set'),
  position: z.enum(['bottom-left', 'top-centre', 'bottom-right']),
  opacity: z.number().min(0).max(1).default(1.0),
  scale: z.number().min(0.8).max(1.2).default(1.0),
});

export const DroneTuneBeat = z.object({
  ...beatBase,
  kind: z.literal('drone_tune'),
  semitones: z.number().int().min(-12).max(12),
  duration_ms: z.number().int().min(1000),
});

export const ThreadAuraBeat = z.object({
  ...beatBase,
  kind: z.literal('thread_aura'),
  color: z.string().regex(/^#[0-9a-f]{6}$/i),
  intensity: z.number().min(0).max(1),
  duration_ms: z.number().int().min(0).default(0),
});

export const HoldBeat = z.object({
  ...beatBase,
  kind: z.literal('hold'),
  duration_ms: z.number().int().min(0).default(0),
  allow_skip: z.boolean().default(false),
});

export const Beat = z.discriminatedUnion('kind', [
  SayBeat,
  SceneBeat,
  SfxBeat,
  SetFlagBeat,
  ChoiceBeat,
  GotoBeat,
  IfFlagBeat,
  EndBeat,
  WaitBeat,
  BondRingUpdateBeat,
  CrestHudSetBeat,
  DroneTuneBeat,
  ThreadAuraBeat,
  HoldBeat,
]);
export type Beat = z.infer<typeof Beat>;

// ── Locale overlay schemas ──

const BeatLocaleOverrideSchema = z.object({
  text: z.string().optional(),
  speaker: z.string().optional(),
  prompt: z.string().optional(),
  /** Parallel to choice options array — just the label strings, by position. */
  options: z.array(z.string()).optional(),
}).strict();

/** keyed by beat id */
const LocaleMapSchema = z.record(z.string(), BeatLocaleOverrideSchema);

/** keyed by locale code e.g. 'zh' */
const LocalesSchema = z.record(z.string(), LocaleMapSchema);

export const Script = z
  .object({
    meta: Meta,
    characters: z.record(z.string(), Character),
    beats: z.array(Beat).min(1),
    locales: LocalesSchema.optional(),
  })
  .superRefine((s, ctx) => {
    const ids = new Set<string>();
    for (const b of s.beats) {
      if (ids.has(b.id))
        ctx.addIssue({ code: 'custom', message: `duplicate beat id ${b.id}` });
      ids.add(b.id);
    }
    // resolve gotos
    for (const b of s.beats) {
      const targets: string[] = [];
      if (b.kind === 'goto') targets.push(b.goto);
      if (b.kind === 'ifFlag') targets.push(b.goto, b.else);
      if (b.kind === 'choice') targets.push(...b.options.map((o) => o.goto));
      for (const t of targets) {
        if (!ids.has(t)) {
          ctx.addIssue({
            code: 'custom',
            message: `unknown goto target ${t} from ${b.id}`,
          });
        }
      }
    }
  });
export type Script = z.infer<typeof Script>;
